import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarX, Plus, Trash2, Pencil, Loader2, AlertTriangle, User, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtTimeRange } from "@/lib/format";

const fld = "rounded-lg border border-[#E5E0D8] px-2.5 py-1.5 text-sm outline-none focus:border-[#144F75] bg-white";
const fmtRange = (b) => b.start_date === b.end_date ? b.start_date : `${b.start_date} → ${b.end_date}`;

export default function BlackoutEditor() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["mgr-blackouts"],
    queryFn: () => api.get("/manager/blackouts").then((r) => r.data),
  });
  const [form, setForm] = useState({ start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });
  const [editing, setEditing] = useState(null); // {id, start_date, end_date, reason}
  const [busy, setBusy] = useState(false);
  const [affected, setAffected] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["mgr-blackouts"] });

  const create = async () => {
    if (!form.start_date) { toast.error("Pick a start date."); return; }
    setBusy(true);
    try {
      const r = await api.post("/manager/blackouts", form);
      toast.success("Dates blocked");
      setAffected(r.data.affected || []);
      setForm({ start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/manager/blackouts/${editing.id}`, editing);
      toast.success("Blackout updated");
      setAffected(r.data.affected || []);
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/manager/blackouts/${id}`);
      toast.success("Blackout removed");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const list = data?.blackouts || [];
  const centerBlocks = list.filter((b) => b.scope === "center");
  const enhBlocks = list.filter((b) => b.scope === "enhancer");

  return (
    <div data-testid="mgr-blackout-panel" className="max-w-3xl">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2 mb-1"><CalendarX className="text-[#B5794E]" /> Blackout dates</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Block dates when your whole center can't take appointments (closures, holidays, maintenance). Clients see these days as unavailable. Existing appointments are <b>never</b> auto-cancelled — you'll be notified to handle them.</p>

      {/* Add form */}
      <div className="eyw-card p-4 mb-5" data-testid="blackout-add-form">
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="blackout-start" className={fld} />
          <span className="text-sm text-[#4A5D6B]">to</span>
          <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="blackout-end" className={fld} />
          <input type="text" placeholder="Reason (optional, internal only)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="blackout-reason" className={`${fld} flex-1 min-w-[180px]`} />
          <button onClick={create} disabled={busy} data-testid="blackout-create-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Block dates
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="text-xs text-[#4A5D6B]">Hours (optional):</span>
          <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="blackout-start-time" className={fld} />
          <span className="text-sm text-[#4A5D6B]">–</span>
          <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="blackout-end-time" className={fld} />
          {(form.start_time || form.end_time) && (
            <button type="button" onClick={() => setForm({ ...form, start_time: "", end_time: "" })} data-testid="blackout-clear-times" className="text-xs text-[#8FA89B] underline">Clear (block whole day)</button>
          )}
        </div>
        <p className="text-[11px] text-[#8FA89B] mt-2">Leave the end date empty to block a single day. Set hours to block only part of the day — other times stay bookable. The reason is only visible to staff.</p>
      </div>

      {affected && affected.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-5" data-testid="blackout-affected-warning">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={15} /> {affected.length} existing appointment{affected.length > 1 ? "s" : ""} fall on the blocked dates</p>
          <p className="text-xs text-amber-700 mt-1 mb-2">They were <b>not</b> cancelled — reschedule or cancel them manually from the Appointments tab.</p>
          <ul className="space-y-1">
            {affected.map((a) => (
              <li key={a.id} className="text-xs text-amber-800" data-testid={`blackout-affected-${a.id}`}>{a.date} {a.start_time} · {a.service_title} — {a.client}{a.enhancer_name ? ` (${a.enhancer_name})` : ""}</li>
            ))}
          </ul>
          <button onClick={() => setAffected(null)} className="text-[11px] text-amber-700 underline mt-2" data-testid="blackout-affected-dismiss">Dismiss</button>
        </div>
      )}

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : (
        <>
          <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Center closures</h3>
          <div className="space-y-2 mb-7" data-testid="blackout-center-list">
            {centerBlocks.length === 0 && <p className="text-sm text-[#8FA89B]">No blocked dates.</p>}
            {centerBlocks.map((b) => (
              <div key={b.id} data-testid={`blackout-row-${b.id}`} className="eyw-card p-3.5">
                {editing?.id === b.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} data-testid={`blackout-edit-start-${b.id}`} className={fld} />
                    <span className="text-sm text-[#4A5D6B]">to</span>
                    <input type="date" value={editing.end_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} data-testid={`blackout-edit-end-${b.id}`} className={fld} />
                    <input type="time" value={editing.start_time} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} data-testid={`blackout-edit-start-time-${b.id}`} className={fld} />
                    <span className="text-sm text-[#4A5D6B]">–</span>
                    <input type="time" value={editing.end_time} onChange={(e) => setEditing({ ...editing, end_time: e.target.value })} data-testid={`blackout-edit-end-time-${b.id}`} className={fld} />
                    <input type="text" placeholder="Reason (internal)" value={editing.reason} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} className={`${fld} flex-1 min-w-[140px]`} data-testid={`blackout-edit-reason-${b.id}`} />
                    <button onClick={saveEdit} disabled={busy} data-testid={`blackout-save-${b.id}`} className="text-[#5C8A72]"><Check size={17} /></button>
                    <button onClick={() => setEditing(null)} data-testid={`blackout-cancel-edit-${b.id}`} className="text-[#8FA89B]"><X size={17} /></button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-semibold text-[#1A242B]">{fmtRange(b)}</span>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${b.start_time ? "bg-[#144F75]/[0.08] text-[#144F75]" : "bg-red-50 text-red-500"}`} data-testid={`blackout-hours-${b.id}`}>{b.start_time ? `${fmtTimeRange(b.start_time, b.end_time)}` : "All day"}</span>
                    {b.reason && <span className="text-xs text-[#4A5D6B] bg-[#F1EDE6] rounded-full px-2 py-0.5" title="Internal reason">{b.reason}</span>}
                    <span className="ml-auto flex items-center gap-2.5">
                      <button onClick={() => setEditing({ id: b.id, start_date: b.start_date, end_date: b.end_date, start_time: b.start_time || "", end_time: b.end_time || "", reason: b.reason || "" })} data-testid={`blackout-edit-${b.id}`} className="text-[#144F75]"><Pencil size={15} /></button>
                      <button onClick={() => remove(b.id)} data-testid={`blackout-delete-${b.id}`} className="text-red-500"><Trash2 size={15} /></button>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Enhancer self-blocks at your center</h3>
          <p className="text-xs text-[#8FA89B] mb-2">Set by enhancers themselves — they only affect that enhancer, not your center's availability.</p>
          <div className="space-y-2" data-testid="blackout-enhancer-list">
            {enhBlocks.length === 0 && <p className="text-sm text-[#8FA89B]">None right now.</p>}
            {enhBlocks.map((b) => (
              <div key={b.id} data-testid={`blackout-enh-row-${b.id}`} className="eyw-card p-3.5 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-sm text-[#1A242B]"><User size={13} className="text-[#D98E73]" /> {b.enhancer_name || "Enhancer"}</span>
                <span className="text-sm text-[#4A5D6B]">{fmtRange(b)}</span>
                {b.start_time && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#144F75]/[0.08] text-[#144F75]">{fmtTimeRange(b.start_time, b.end_time)}</span>}
                {b.reason && <span className="text-xs text-[#4A5D6B] bg-[#F1EDE6] rounded-full px-2 py-0.5">{b.reason}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
